import { Furniture } from "@/state/Furniture";
import { FileLoader } from "three/src/loaders/FileLoader";
import { v4 as uuidv4 } from "uuid";
import { loadModelWithConfig } from "./model";
import { client } from "./client";
import { ResponseContract } from "./contracts";
import { parseDimensions } from "@/utils/dimensions";

export type FurnitureDto = {
    group: string | null; // furniture name
    category: string | null;
    id: number;
    name: string | null; // variant name
    code: string | null;
    price: number;
    dimensions: string | null;
    description: string | null;
    colors: string[] | null;
    isHidden: boolean;
    previewImageUrl: string | null;
    modelUrl: string | null;
    modelConfigUrl: string | null;
    catalogImageUrls: string[] | null;
    variantParameterName?: string | null;
    variantParameterImageUrl?: string | null;
};

const rootPath = "/assets/";

export const uploadFurnitureExcel = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    await client.post("api/furniture/import", formData);
};

export const loadFurnitureDataFromStaticJson = async (
    fileName: string
): Promise<ReadonlyArray<FurnitureDto>> => {
    const loader = new FileLoader();
    let content: string | ArrayBuffer = "";

    try {
        content = await loader.loadAsync(rootPath + fileName);
    } catch {
        return [];
    }

    if (typeof content !== "string") {
        console.error("content is not string");
        return [];
    }

    let furniture: ReadonlyArray<FurnitureDto> = [];

    try {
        furniture = JSON.parse(content);
        if (!Array.isArray(furniture)) {
            console.error("furniture is not array");
            furniture = [];
        }
    } catch (e) {
        console.error("Failed to parse furniture data", e);
    }

    return furniture;
};

export const loadFurnitureDataFromApi = async (): Promise<ReadonlyArray<FurnitureDto>> => {
    const response = await client.get<ResponseContract<ReadonlyArray<FurnitureDto>>>(
        "api/furniture"
    );

    return response.data.data;
};

type FurnitureData = Pick<Furniture, "id" | "data" | "resources">;

export const initFurniture = async (
    data: ReadonlyArray<FurnitureDto>,
    onProgress: (e: number) => void
): Promise<Record<string, Furniture>> => {
    const furnitureGroups: Record<string, { variants: FurnitureData[] }> = {
        default: { variants: [] },
    };

    data.forEach((dto) => {
        if (!dto.code) {
            return;
        }

        const variant: FurnitureData = {
            id: `${dto.code}-${uuidv4()}`,
            data: {
                name: dto.name ?? dto.code,
                code: dto.code,
                price: dto.price,
                dimensions: dto.dimensions
                    ? parseDimensions(dto.dimensions)
                    : parseDimensions("0x0x0 mm"),
                description: dto.description ?? "",
                colors: dto.colors ?? [],
                isHidden: dto.isHidden,
                category: dto.category ?? "",
                variantParameterName: dto.variantParameterName ?? undefined,
                variantParameterImageUrl: dto.variantParameterImageUrl ?? undefined,
            },
            resources: {
                previewPath: dto.previewImageUrl ?? "",
                modelPath: dto.modelUrl ?? "",
                configPath: dto.modelConfigUrl ?? "",
                catalogImagePaths: dto.catalogImageUrls ?? [],
            },
        };

        const group = dto.group ?? dto.code;

        if (!furnitureGroups[group]) {
            furnitureGroups[group] = { variants: [] };
        }
        furnitureGroups[group].variants.push(variant);
    });

    const furniture: Record<string, Furniture> = {};
    const furnitureArray = Object.values(furnitureGroups);

    const counter = { count: 0 };

    await Promise.all(
        Object.values(furnitureGroups).flatMap(async (group) => {
            for (const variant of group.variants) {
                const data =
                    variant.data.category === "System"
                        ? await loadModelWithConfig({
                              modelPath: variant.resources.modelPath,
                              configPath: variant.resources.configPath,
                          })
                        : undefined;

                const item: Furniture = {
                    id: variant.id,
                    otherVariantIds: group.variants
                        .filter((v) => v.id !== variant.id)
                        .map((v) => v.id),
                    data: variant.data,
                    resources: variant.resources,
                    model: data,
                };

                furniture[variant.id] = item;
            }

            counter.count++;
            onProgress(counter.count / furnitureArray.length);
        })
    );

    return furniture;
};
